import { Download, FileCheck, File, ChevronLeft } from 'lucide-react';
import { fmtBRL } from '@/lib/utils';
import { useFasesCatalog } from '@/lib/hooks/prestacoes';
import { PrestTimeline } from './PrestTimeline';
import type { PrestHistoryItemData } from './types';

interface PrestDetailModuleProps {
  prest: PrestHistoryItemData;
  workspaceId: string | null;
  onBack: () => void;
}

interface DocItem {
  title: string;
  size: string;
  icon: typeof FileCheck;
  /** TODO: integrar URL real do R2 */
  url?: string;
}

const MOCK_DOCS: DocItem[] = [
  { title: 'Comprovante do portal', size: 'PDF · 240 KB', icon: FileCheck },
  { title: 'Livro caixa', size: 'PDF · 1,2 MB', icon: File },
  { title: 'Balancete', size: 'PDF · 380 KB', icon: File },
  { title: 'Demonstrativo de receitas', size: 'XLSX · 88 KB', icon: File },
];

function KpiCard({
  label,
  value,
  color,
}: {
  label: string;
  value: string;
  color: string;
}) {
  return (
    <div
      style={{
        background: 'var(--card)',
        border: '1px solid var(--card-stroke)',
        borderRadius: 'var(--r-3)',
        padding: 12,
      }}
    >
      <div
        style={{
          fontSize: 9.5,
          textTransform: 'uppercase',
          letterSpacing: '0.07em',
          color: 'var(--fg-muted)',
          fontFamily: 'var(--font-mono)',
          fontWeight: 600,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 14,
          fontWeight: 700,
          marginTop: 4,
          color,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </div>
    </div>
  );
}

/**
 * Detalhe de prestação concluída (histórico).
 * Timeline read-only (allDone=true), 3 KPIs e lista de documentos.
 * SEM botão Concluir. SEM iframe Steel.
 */
export function PrestDetailModule({ prest, workspaceId, onBack }: PrestDetailModuleProps) {
  const fasesQ = useFasesCatalog(workspaceId);
  const fasesCatalog = fasesQ.data?.items ?? [];

  /* KPIs mock enquanto não existe endpoint de snapshot */
  // TODO: integrar snapshot real de KPIs quando disponível na API
  const receitas = 41200;
  const despesas = 29870;
  const saldo = receitas - despesas;

  return (
    <div
      style={{
        height: '100%',
        overflowY: 'auto',
        minHeight: 0,
        padding: '8px 18px 24px',
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
      }}
    >
      {/* Header com back */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          paddingTop: 4,
        }}
      >
        <button
          type="button"
          onClick={onBack}
          aria-label="Voltar para lista de prestações"
          style={{
            width: 34,
            height: 34,
            borderRadius: 'var(--r-pill)',
            background: 'var(--card-soft)',
            border: '1px solid var(--card-stroke)',
            display: 'grid',
            placeItems: 'center',
            color: 'var(--fg-muted)',
            cursor: 'pointer',
            flexShrink: 0,
            minHeight: 44,
            minWidth: 44,
          }}
        >
          <ChevronLeft size={16} strokeWidth={1.8} aria-hidden />
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 10,
              letterSpacing: '0.10em',
              textTransform: 'uppercase',
              fontWeight: 600,
              color: 'var(--fg-muted)',
              marginBottom: 2,
            }}
          >
            Prestação · concluída
          </div>
          <div
            style={{
              fontSize: 18,
              fontWeight: 700,
              letterSpacing: '-0.02em',
              color: 'var(--fg)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {prest.label}
          </div>
        </div>
      </div>

      {/* Card hero com protocolo */}
      <div
        style={{
          background: 'var(--card)',
          border: '1px solid var(--card-stroke)',
          borderRadius: 'var(--r-3)',
          padding: 16,
          boxShadow: 'var(--shadow-clay)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 10,
          }}
        >
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              padding: '3px 10px',
              borderRadius: 'var(--r-pill)',
              fontSize: 10.5,
              fontWeight: 700,
              fontFamily: 'var(--font-mono)',
              background: 'var(--pass-soft)',
              color: 'var(--pass)',
            }}
          >
            <span
              style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--pass)' }}
              aria-hidden
            />
            concluída
          </span>
          {prest.concluidaEm && (
            <span
              style={{
                fontSize: 11,
                fontFamily: 'var(--font-mono)',
                color: 'var(--fg-muted)',
              }}
            >
              {new Date(prest.concluidaEm).toLocaleDateString('pt-BR')}
            </span>
          )}
        </div>

        <div
          style={{
            fontSize: 10,
            textTransform: 'uppercase',
            letterSpacing: '0.07em',
            color: 'var(--fg-muted)',
            fontFamily: 'var(--font-mono)',
            fontWeight: 600,
          }}
        >
          Competência
        </div>
        <div
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 16,
            fontWeight: 700,
            letterSpacing: '-0.01em',
            marginTop: 2,
            color: 'var(--fg)',
          }}
        >
          {prest.competencia}
        </div>
      </div>

      {/* 3 KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
        <KpiCard label="Receitas" value={fmtBRL(receitas)} color="var(--pass)" />
        <KpiCard label="Despesas" value={fmtBRL(despesas)} color="var(--fg)" />
        <KpiCard label="Saldo" value={fmtBRL(saldo)} color="var(--mint-deep)" />
      </div>

      {/* Timeline read-only */}
      <div
        style={{
          background: 'var(--card)',
          border: '1px solid var(--card-stroke)',
          borderRadius: 'var(--r-3)',
          padding: 14,
        }}
      >
        <div
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 10,
            letterSpacing: '0.10em',
            textTransform: 'uppercase',
            fontWeight: 600,
            color: 'var(--fg-muted)',
            marginBottom: 12,
          }}
        >
          Fases · 16 de 16 concluídas
        </div>
        {fasesCatalog.length > 0 ? (
          <PrestTimeline fases={fasesCatalog} allDone />
        ) : (
          /* Fallback se catálogo ainda não carregou */
          <p style={{ fontSize: 12, color: 'var(--fg-muted)', margin: 0 }}>
            Carregando fases…
          </p>
        )}
      </div>

      {/* Documentos gerados */}
      <div>
        <div
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 10,
            letterSpacing: '0.10em',
            textTransform: 'uppercase',
            fontWeight: 600,
            color: 'var(--fg-muted)',
            marginBottom: 10,
          }}
        >
          Documentos gerados
        </div>
        {/* TODO: integrar lista real de documentos via API */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {MOCK_DOCS.map(({ title, size, icon: Icon, url }) => (
            <a
              key={title}
              href={url ?? '#'}
              target={url ? '_blank' : undefined}
              rel="noopener noreferrer"
              aria-label={`Baixar ${title}`}
              style={{
                textAlign: 'left',
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '12px',
                background: 'var(--card)',
                border: '1px solid var(--card-stroke)',
                borderRadius: 'var(--r-3)',
                textDecoration: 'none',
                minHeight: 44,
              }}
            >
              <span
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 10,
                  background: 'var(--card-soft)',
                  color: 'var(--fg-muted)',
                  display: 'grid',
                  placeItems: 'center',
                  flexShrink: 0,
                }}
              >
                <Icon size={16} strokeWidth={1.8} aria-hidden />
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 12.5,
                    fontWeight: 600,
                    color: 'var(--fg)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {title}
                </div>
                <div
                  style={{
                    fontSize: 10,
                    fontFamily: 'var(--font-mono)',
                    color: 'var(--fg-muted)',
                    marginTop: 2,
                  }}
                >
                  {size}
                </div>
              </div>
              <Download size={15} style={{ color: 'var(--fg-dim)', flexShrink: 0 }} aria-hidden />
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}
